import type { IAppleSubscriptionInfo } from '@/lib/api/apple-billing.client';
import { useSessionStore } from '@/lib/stores/session.store';

/** The fields that tell it; the session profile and the admin workspace list carry all three. */
interface BillingState {
  billingType?: string | null;
  billingStatus?: string | null;
  suspensionReason?: string | null;
}

/**
 * Whether the workspace pays through an App Store subscription that Apple has
 * not ended yet (KAN-10): billed by Apple and active, in Apple's grace period
 * (PAST_DUE), or in Apple's billing retry (SUSPENDED for a failed payment).
 * Apple keeps trying to charge in the retry for weeks, so the workspace is
 * locked but the subscription still counts.
 *
 * While it runs, nothing may be bought through Stripe and nobody may be added
 * to the workspace. The server refuses both with APPLE_SUBSCRIPTION_ACTIVE, so
 * the UI must not offer them in the first place. Once Apple has really ended
 * it (expired or refunded: suspended as cancelled, the billing type stays
 * APPLE), the purchase on the web works again and moves it onto Stripe.
 */
export function isAppleSubscriptionRunning(state: BillingState | null | undefined): boolean {
  if (state?.billingType !== 'APPLE') return false;
  if (state.billingStatus === 'ACTIVE' || state.billingStatus === 'PAST_DUE') return true;
  return state.billingStatus === 'SUSPENDED' && state.suspensionReason === 'PAYMENT_FAILED';
}

/** {@link isAppleSubscriptionRunning} for the signed-in workspace, re-rendering when it changes. */
export function useAppleSubscriptionRunning(): boolean {
  return useSessionStore((state) => isAppleSubscriptionRunning(state.profile));
}

/**
 * The subscription itself, as Apple last reported it: one Apple still bills
 * or still tries to bill. Expired and refunded ones are history.
 */
export function appleSubscriptionLive(subscription?: IAppleSubscriptionInfo | null): boolean {
  return (
    subscription?.state === 'active' ||
    subscription?.state === 'grace' ||
    subscription?.state === 'billing_retry'
  );
}
